import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { MessageCircleWarning, Loader2, Send, X } from 'lucide-react';
import { feedbackAPI } from '../api/feedback';
import { useNotification } from '../contexts/NotificationContext';

const DESCRIPTION_LIMIT = 2000;
const CATEGORY_OPTIONS = [
  { value: 'general', label: '功能异常' },
  { value: 'ui', label: '界面问题' },
  { value: 'performance', label: '性能/速度' },
  { value: 'billing', label: '计费/额度' },
  { value: 'other', label: '其他' },
];

const BugReportWidget: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [description, setDescription] = useState('');
  const [contact, setContact] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [pageUrl, setPageUrl] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.href : ''
  );
  const [userAgent, setUserAgent] = useState<string>(() =>
    typeof window !== 'undefined' ? window.navigator.userAgent : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPageUrl(window.location.href);
    setUserAgent(window.navigator.userAgent);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    return () => {
      if (screenshotPreview) {
        URL.revokeObjectURL(screenshotPreview);
      }
    };
  }, [screenshotPreview]);

  const descriptionRemaining = useMemo(
    () => Math.max(0, DESCRIPTION_LIMIT - description.length),
    [description]
  );

  const handleClose = () => {
    if (submitting) return;
    setIsOpen(false);
  };

  const resetForm = () => {
    setCategory('general');
    setDescription('');
    setContact('');
    setScreenshotUrl('');
    setScreenshotPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return '';
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeScreenshot = useCallback(() => {
    setScreenshotPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return '';
    });
    setScreenshotUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleScreenshotChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('只支持上传图片文件');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('图片大小需小于 5MB');
      event.target.value = '';
      return;
    }

    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setScreenshotPreview(previewUrl);
    setUploadingScreenshot(true);
    try {
      const { url } = await feedbackAPI.uploadScreenshot(file);
      setScreenshotUrl(url);
      showSuccess('截图上传成功');
    } catch (error: any) {
      showError(error?.response?.data?.detail || error?.message || '截图上传失败，请稍后再试');
      removeScreenshot();
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (description.trim().length < 10) {
      showError('请至少填写 10 个字符的描述，方便我们定位问题。');
      return;
    }
    setSubmitting(true);
    try {
      await feedbackAPI.submit({
        category,
        description: description.trim(),
        contact: contact.trim() || undefined,
        screenshot_url: screenshotUrl.trim() || undefined,
        page_url: pageUrl,
        user_agent: userAgent,
      });
      showSuccess('反馈已提交，感谢你的帮助！');
      resetForm();
      setIsOpen(false);
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        '提交失败，请稍后再试。';
      showError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) {
    return null;
  }

  return createPortal(
    <div className="fixed bottom-6 right-4 sm:right-6 z-[70] text-sm font-sans">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
        >
          <MessageCircleWarning className="w-4 h-4" />
          反馈问题
        </button>
      )}

      {isOpen && (
        <div className="w-[320px] sm:w-[360px] bg-white rounded-2xl shadow-2xl border border-gray-100 animate-slide-up">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <MessageCircleWarning className="w-4 h-4 text-primary-500" />
              问题反馈
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600"
              aria-label="关闭反馈窗口"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form className="px-4 py-3 space-y-3" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                问题类型
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                问题描述 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(
                  e.target.value.slice(0, DESCRIPTION_LIMIT)
                )}
                rows={4}
                placeholder="请描述你遇到的问题、重现步骤等信息"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400 resize-none"
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                还可输入 {descriptionRemaining} 字
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                截图（可选）
              </label>
              <div className="border border-dashed border-gray-300 rounded-xl p-3 text-center">
                {screenshotPreview ? (
                  <div className="relative">
                    <img
                      src={screenshotPreview}
                      alt="截图预览"
                      className="w-full h-32 object-contain rounded-lg bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 hover:bg-gray-700"
                      aria-label="移除截图"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full text-gray-500 text-sm py-6 flex flex-col items-center justify-center gap-2 hover:text-primary-600"
                    disabled={uploadingScreenshot}
                  >
                    <span className="text-3xl">🖼️</span>
                    <span>点击上传或拖拽图片</span>
                    <span className="text-xs text-gray-400">支持 PNG/JPG/WebP，最大 5MB</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleScreenshotChange}
                />
                {uploadingScreenshot && (
                  <div className="flex items-center justify-center gap-2 text-xs text-primary-600 mt-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    正在上传截图...
                  </div>
                )}
                {screenshotUrl && !uploadingScreenshot && (
                  <p className="text-xs text-green-600 mt-2 break-all">
                    已上传：{screenshotUrl}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                联系方式（可选）
              </label>
              <input
                type="text"
                placeholder="邮箱 / 微信 / Telegram"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              />
            </div>

            <div className="bg-gray-50 rounded-xl px-3 py-2 text-xs text-gray-500">
              <div>页面：{pageUrl || '未知'}</div>
              <div className="truncate">浏览器：{userAgent || '未知'}</div>
            </div>

            <button
              type="submit"
              disabled={submitting || uploadingScreenshot}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl shadow hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  提交中...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  发送反馈
                </>
              )}
            </button>

            <p className="text-[11px] text-gray-400 text-center leading-relaxed">
              我们会尽快查看你的反馈。提交后会自动记录用户与设备信息，帮助定位问题。
            </p>
          </form>
        </div>
      )}
    </div>,
    portalTarget
  );
};

export default BugReportWidget;

